// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
export const REGULATORY_FEE_WORDS = {
    // ── Frame and boundary (every document) ─────────────────────
    exportFrame: {
        en: 'A copy of the payment lines your organization recorded, as the register shows them. The platform sends this document to no one.',
        vi: 'Bản sao các khoản nộp mà tổ chức bạn đã ghi nhận, đúng như sổ hiển thị. Nền tảng không gửi tài liệu này cho bất kỳ ai.',
    },
    boundary: {
        en: 'The platform records payments against the installed fee schedule; it never prices, decides or files. It pays nothing, files nothing and verifies no receipt. The scheduled amount is the installed schedule’s amount for the row and quantity a person chose; the amount paid is what a person recorded; a difference is shown as recorded and never adjusted.',
        vi: 'Nền tảng ghi nhận các khoản nộp theo biểu phí đã cài đặt; nền tảng không định giá, không quyết định và không nộp hồ sơ. Nền tảng không nộp tiền, không nộp hồ sơ và không xác minh chứng từ thu nào. Mức thu theo biểu là số tiền mà biểu phí đã cài đặt quy định cho dòng và số lượng do người dùng chọn; số tiền đã nộp là số tiền người dùng ghi nhận; chênh lệch được hiển thị theo số liệu đã ghi nhận và không bao giờ được điều chỉnh.',
    },
    paymentNotOutcome: {
        en: 'A recorded payment is evidence of payment only. It is not a licence, a certificate or a decision, and recording one sets no decision.',
        vi: 'Khoản nộp đã ghi nhận chỉ là bằng chứng về việc đã nộp tiền. Khoản nộp đó không phải là giấy phép, giấy chứng nhận hay quyết định, và việc ghi nhận khoản nộp không xác lập quyết định nào.',
    },
    decisionApart: {
        en: 'The authority’s decision is printed in its own section, as a person recorded it. No payment sets or changes it.',
        vi: 'Quyết định của cơ quan có thẩm quyền được trình bày tại mục riêng, theo nội dung người dùng đã ghi nhận. Không khoản nộp nào xác lập hay thay đổi quyết định đó.',
    },
    noTotal: {
        en: 'Each line is listed as recorded. No total, sum or balance is computed.',
        vi: 'Mỗi khoản nộp được liệt kê theo nội dung đã ghi nhận. Nền tảng không tính tổng, cộng dồn hay số dư.',
    },
    noneDeclared: {
        en: 'No installed framework declares a fee schedule, so no payment line can be recorded. Lines recorded earlier stay listed.',
        vi: 'Không có khung tuân thủ đã cài đặt nào khai báo biểu phí, nên không thể ghi nhận khoản nộp nào. Các khoản nộp đã ghi nhận trước đó vẫn được liệt kê.',
    },
    // ── Titles, metadata and captions ───────────────────────────
    titleApplication: {
        en: 'Fee lines and decision — application {ref}',
        vi: 'Các khoản phí, lệ phí và quyết định — hồ sơ đề nghị {ref}',
    },
    titleCertificate: {
        en: 'Charge lines — sender-name certificate {name}',
        vi: 'Các khoản lệ phí — giấy chứng nhận tên định danh {name}',
    },
    titlePeriod: {
        en: 'Fee and charge lines paid in {period}',
        vi: 'Các khoản phí, lệ phí có ngày nộp tiền trong tháng {period}',
    },
    exportOrganization: { en: 'Organization: {name}', vi: 'Tổ chức: {name}' },
    exportGeneratedAt: { en: 'Generated at: {instant}', vi: 'Thời điểm tạo: {instant}' },
    exportGeneratedBy: { en: 'Generated by: {name}', vi: 'Người tạo: {name}' },
    exportPeriod: { en: 'Paid-on month: {period}', vi: 'Tháng nộp tiền: {period}' },
    exportLineCount: { en: 'Payment lines: {count}', vi: 'Số khoản nộp: {count}' },
    exportField: { en: 'Field', vi: 'Trường' },
    exportValue: { en: 'Value', vi: 'Giá trị' },
    captionApplication: {
        en: 'The application, as recorded',
        vi: 'Hồ sơ đề nghị, theo nội dung đã ghi nhận',
    },
    captionCertificate: {
        en: 'The certificate, as recorded',
        vi: 'Giấy chứng nhận, theo nội dung đã ghi nhận',
    },
    captionFeeLines: { en: 'Fee lines', vi: 'Các khoản phí, lệ phí' },
    captionChargeLines: { en: 'Charge lines', vi: 'Các khoản lệ phí' },
    captionDecision: {
        en: 'The authority’s decision, as recorded',
        vi: 'Quyết định của cơ quan có thẩm quyền, theo nội dung đã ghi nhận',
    },
    captionPeriodLines: {
        en: 'Payment lines paid in this month',
        vi: 'Các khoản nộp có ngày nộp tiền trong tháng này',
    },
    captionPeriodDecisions: {
        en: 'The authority’s decision on each application listed, as recorded',
        vi: 'Quyết định của cơ quan có thẩm quyền đối với từng hồ sơ đề nghị được liệt kê, theo nội dung đã ghi nhận',
    },
    // ── Record fields ───────────────────────────────────────────
    fieldRecordKind: { en: 'Record', vi: 'Bản ghi' },
    fieldRecordReference: { en: 'Reference', vi: 'Số hồ sơ' },
    fieldApplicationReference: { en: 'Application reference', vi: 'Số hồ sơ' },
    fieldProcedure: { en: 'Procedure', vi: 'Thủ tục' },
    fieldFiledOn: { en: 'Filing date', vi: 'Ngày nộp hồ sơ' },
    fieldLicence: { en: 'Licence on the register', vi: 'Giấy phép trong sổ' },
    fieldPendingAtCommencement: {
        en: 'Pending at commencement',
        vi: 'Đang chờ xử lý tại thời điểm có hiệu lực',
    },
    fieldTransitionalProvision: { en: 'Transitional provision', vi: 'Quy định chuyển tiếp' },
    fieldSenderName: { en: 'Sender name', vi: 'Tên định danh' },
    fieldCertificateReference: { en: 'Certificate number', vi: 'Số giấy chứng nhận' },
    fieldGrantedOn: { en: 'Granted on', vi: 'Ngày cấp' },
    fieldValidUntil: { en: 'Valid until', vi: 'Có giá trị đến' },
    fieldIssuingBody: { en: 'Issuing body', vi: 'Cơ quan cấp' },
    fieldNotes: { en: 'Notes', vi: 'Ghi chú' },
    fieldDecisionStatus: { en: 'Decision', vi: 'Quyết định' },
    fieldDecidedOn: { en: 'Decision date', vi: 'Ngày quyết định' },
    fieldDecisionReference: { en: 'Decision reference', vi: 'Số quyết định' },
    fieldDecisionDocument: { en: 'Decision document', vi: 'Văn bản quyết định' },
    fieldRecordedBy: { en: 'Recorded by', vi: 'Người ghi nhận' },
    fieldRecordedAt: { en: 'Recorded on', vi: 'Ngày ghi nhận' },
    fieldRefund: { en: 'Refund', vi: 'Hoàn trả' },
    // ── Line columns (the 62.54.0 table's headings) ─────────────
    colRow: { en: 'Schedule row', vi: 'Dòng trong biểu' },
    colQuantity: { en: 'Quantity', vi: 'Số lượng' },
    colScheduled: {
        en: 'Scheduled amount for this row and quantity',
        vi: 'Mức thu theo biểu cho dòng và số lượng này',
    },
    colPaid: { en: 'Amount paid', vi: 'Số tiền đã nộp' },
    colDifference: { en: 'Difference, as recorded', vi: 'Chênh lệch, theo số liệu đã ghi nhận' },
    colUnit: { en: 'Unit', vi: 'Đơn vị' },
    colPayerRule: { en: 'Who pays, as the row declares it', vi: 'Người nộp, theo khai báo của dòng' },
    colPayer: { en: 'Payer', vi: 'Người nộp' },
    colPaidOn: { en: 'Paid on', vi: 'Ngày nộp tiền' },
    colCollectingBody: { en: 'Collecting body', vi: 'Tổ chức thu phí, lệ phí' },
    colReceiptNumber: { en: 'Receipt number', vi: 'Số chứng từ thu' },
    colReceipt: { en: 'Receipt', vi: 'Chứng từ thu' },
    colRateDate: { en: 'Rate in force on', vi: 'Mức thu có hiệu lực vào ngày' },
    // ── Values ──────────────────────────────────────────────────
    none: { en: '—', vi: '—' },
    yes: { en: 'Yes', vi: 'Có' },
    no: { en: 'No', vi: 'Không' },
    notRecorded: { en: 'Not recorded', vi: 'Chưa ghi nhận' },
    recordNotFound: { en: 'No longer found', vi: 'Không còn tìm thấy' },
    ownerLicenceApplication: { en: 'Licence application', vi: 'Hồ sơ đề nghị cấp phép' },
    ownerSenderNameCertificate: {
        en: 'Sender-name certificate',
        vi: 'Giấy chứng nhận tên định danh',
    },
    noRow: {
        en: 'No installed row — amount paid only',
        vi: 'Không có dòng trong biểu đã cài đặt — chỉ ghi số tiền đã nộp',
    },
    bodyFromSchedule: { en: 'named by the installed schedule', vi: 'theo biểu phí đã cài đặt' },
    bodyAsRecorded: { en: 'as recorded', vi: 'theo nội dung đã ghi nhận' },
    receiptHeld: { en: 'Receipt file recorded', vi: 'Đã ghi nhận tệp chứng từ thu' },
    receiptNone: { en: 'No receipt recorded', vi: 'Chưa ghi nhận chứng từ thu' },
    decisionDocumentHeld: { en: 'Decision document recorded', vi: 'Đã ghi nhận văn bản quyết định' },
    decisionDocumentNone: {
        en: 'No decision document recorded',
        vi: 'Chưa ghi nhận văn bản quyết định',
    },
    decision_pending: { en: 'Pending', vi: 'Chờ xử lý' },
    decision_issued: { en: 'Issued', vi: 'Đã cấp' },
    decision_refused: { en: 'Refused', vi: 'Bị từ chối' },
    refundNone: {
        en: 'No refund is provided by the instruments read.',
        vi: 'Các văn bản đã đọc không quy định việc hoàn trả.',
    },
    payer_applicant_on_filing: {
        en: 'Paid by the applicant when the dossier is filed',
        vi: 'Do tổ chức, cá nhân đề nghị nộp khi nộp hồ sơ đề nghị',
    },
    payer_certificate_grantee_no_moment_stated: {
        en: 'Paid by the person granted the certificate; no moment is stated',
        vi: 'Do tổ chức, cá nhân được cấp giấy chứng nhận nộp; văn bản không nêu thời điểm nộp',
    },
    payerRuleNotDeclared: {
        en: 'Who pays is not declared for this row',
        vi: 'Dòng này không khai báo ai là người nộp',
    },
};
/**
 * One word in `locale` (`vi`, `vi-VN` → VI; anything else → EN), with each
 * `{name}` filled from `values`. A placeholder with no value stays as written,
 * so a missing value is visible rather than silently dropped.
 */
export function regulatoryFeeWord(key, locale, values = {}) {
    const pair = REGULATORY_FEE_WORDS[key];
    const text = locale === 'vi' || locale.startsWith('vi-') ? pair.vi : pair.en;
    return text.replace(/\{(\w+)\}/g, (whole, name) => values[name] ?? whole);
}
//# sourceMappingURL=regulatory-fee-words.js.map